/**
 * Daily Attendance Detailed Report — eSSL Excel-compatible.
 * Route: /hr/daily-attendance-report
 *
 * Mirrors the columns of the eSSL software's "Daily Attendance Report
 * (Detailed Report)" Excel so existing HR users have zero learning curve.
 * Adds: search, filter, export to Excel.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  App, Button, Card, DatePicker, Form, Input, Select, Space, Switch, Table,
  Tag, Tooltip, Typography,
} from 'antd'
import {
  DownloadOutlined, FileExcelOutlined, ReloadOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import { dailyAttendanceReportApi, type DailyAttendanceRow } from '@/features/hr/manpowerApi'
import PageHeader from '@/shared/components/PageHeader'
import apiClient from '@/shared/api/client'

const { Text } = Typography

const STATUS_COLOR = (s: string): string => {
  const base = s.replace(' *', '')
  if (base === 'Present') return 'green'
  if (base === 'Present (in)') return 'blue'    // already crossed half-day, still inside
  if (base === 'Working') return 'cyan'         // currently inside, < 4h so far
  if (base === 'Half Day') return 'gold'
  if (base === 'Absent') return 'red'
  return 'default'
}

export default function DailyAttendanceReportPage() {
  const { message } = App.useApp()
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
  const [onlyPresent, setOnlyPresent] = useState(true)
  const [search, setSearch] = useState('')
  const [downloading, setDownloading] = useState(false)
  // Click the Anomalies pill in the summary bar to filter table down to
  // just the asterisk-suffixed rows (unpaired IN/OUT punches).
  const [anomaliesOnly, setAnomaliesOnly] = useState(false)
  // Click any of the count pills (Working / Present / Half Day / Absent)
  // to filter to that status. Null = show everything.
  const [statusPillFilter, setStatusPillFilter] = useState<string | null>(null)

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['daily-attendance-report', date, onlyPresent],
    queryFn: () =>
      dailyAttendanceReportApi.fetch({
        on_date: date,
        only_present: onlyPresent,
      }),
  })

  const allRows = data?.rows ?? []
  // Apply 3 stacked filters: search text → anomalies-only → status pill.
  // Order matters: text first (cheapest, narrows most), then asterisks,
  // then the status pill (each row's status already has the * stripped
  // before compare so 'Present *' counts as 'Present').
  let filtered = allRows
  if (search) {
    const q = search.toLowerCase()
    filtered = filtered.filter((r) => (
      r.name.toLowerCase().includes(q)
      || (r.emp_code ?? '').toLowerCase().includes(q)
      || (r.biometric_id ?? '').toLowerCase().includes(q)
      || (r.department_name ?? '').toLowerCase().includes(q)
    ))
  }
  if (anomaliesOnly) {
    filtered = filtered.filter((r) => r.status.endsWith(' *'))
  }
  if (statusPillFilter) {
    filtered = filtered.filter((r) => r.status.replace(' *', '').startsWith(statusPillFilter))
  }

  const onDownload = async () => {
    setDownloading(true)
    try {
      // We fetch through apiClient so the auth token is included
      const res = await apiClient.get<Blob>('/hr/daily-attendance-report.xlsx', {
        params: { on_date: date, only_present: onlyPresent },
        responseType: 'blob',
      })
      const blob = res.data
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Daily Attendance Report ${dayjs(date).format('DD.MM.YYYY')}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      message.success('Excel downloaded')
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err.response?.data?.detail ?? 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  const columns = [
    {
      title: 'SNo', dataIndex: 'sno', key: 'sno', width: 60, fixed: 'left' as const,
    },
    {
      title: 'E. Code', dataIndex: 'biometric_id', key: 'biometric_id', width: 90,
      render: (v: string | null, r: DailyAttendanceRow) =>
        <code style={{ fontSize: 12 }}>{v ?? r.emp_code}</code>,
    },
    {
      title: 'Name', dataIndex: 'name', key: 'name', width: 220,
      render: (v: string) => <Text strong>{v}</Text>,
      fixed: 'left' as const,
    },
    {
      title: 'Dept', dataIndex: 'department_name', key: 'dept', width: 140, ellipsis: true,
      render: (v: string) => <Tooltip title={v}>{v}</Tooltip>,
    },
    {
      title: 'Shift', dataIndex: 'shift_code', key: 'shift', width: 70,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'S.InTime', dataIndex: 's_in_time', key: 's_in', width: 80,
      render: (v: string) => v || '—',
    },
    {
      title: 'S.OutTime', dataIndex: 's_out_time', key: 's_out', width: 80,
      render: (v: string) => v || '—',
    },
    {
      title: 'A.InTime', dataIndex: 'a_in_time', key: 'a_in', width: 80,
      render: (v: string) => v ? <Text strong style={{ color: '#1677ff' }}>{v}</Text> : '—',
    },
    {
      title: 'A.OutTime', dataIndex: 'a_out_time', key: 'a_out', width: 80,
      render: (v: string) => v ? <Text strong style={{ color: '#1677ff' }}>{v}</Text> : '—',
    },
    {
      title: 'Work', dataIndex: 'work_duration', key: 'work', width: 70,
      render: (v: string) => v,
    },
    {
      title: 'OT', dataIndex: 'overtime', key: 'ot', width: 60,
      render: (v: string) => v !== '00:00' ? <Text style={{ color: '#1677ff' }}>{v}</Text> : v,
    },
    {
      title: 'Total', dataIndex: 'total_duration', key: 'total', width: 70,
    },
    {
      title: 'LateBy', dataIndex: 'late_by', key: 'late', width: 70,
      render: (v: string) => v !== '00:00' ? <Text type="danger">{v}</Text> : v,
    },
    {
      title: 'EarlyOut', dataIndex: 'early_going_by', key: 'early', width: 80,
      render: (v: string) => v !== '00:00' ? <Text type="warning">{v}</Text> : v,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v: string, r: DailyAttendanceRow) => {
        const isAnomaly = v.endsWith(' *')
        const tag = <Tag color={STATUS_COLOR(v)}>{v}</Tag>
        if (isAnomaly && r.anomaly_reason) {
          return (
            <Tooltip
              title={<div style={{ maxWidth: 320 }}>{r.anomaly_reason}</div>}
              color="#cf1322"
            >
              {tag}
            </Tooltip>
          )
        }
        return tag
      },
      filters: [
        { text: 'Working (in now)', value: 'Working' },
        { text: 'Present', value: 'Present' },
        { text: 'Present (in)', value: 'Present (in)' },
        { text: 'Half Day', value: 'Half Day' },
        { text: 'Absent', value: 'Absent' },
        { text: 'Anomaly only (*)', value: '__ANOMALY__' },
      ],
      onFilter: (val: boolean | React.Key, r: DailyAttendanceRow) => (
        val === '__ANOMALY__'
          ? r.status.endsWith(' *')
          : r.status.replace(' *', '') === val
      ),
    },
    {
      title: 'Punch Records', dataIndex: 'punch_records', key: 'punches', width: 360, ellipsis: true,
      render: (v: string) => v
        ? <Tooltip title={<div style={{ maxWidth: 480 }}>{v}</div>}>
            <span style={{ fontSize: 11, color: '#555' }}>{v}</span>
          </Tooltip>
        : <Text type="secondary">—</Text>,
    },
  ]

  // Summary stats
  const totalRows = allRows.length
  const workingCount = allRows.filter((r) => r.status.startsWith('Working')).length
  const presentCount = allRows.filter((r) => r.status.startsWith('Present')).length
  const halfDayCount = allRows.filter((r) => r.status.startsWith('Half Day')).length
  const absentCount = allRows.filter((r) => r.status.startsWith('Absent')).length
  const anomalyCount = allRows.filter((r) => r.status.endsWith(' *')).length

  return (
    <div>
      <PageHeader
        title="Daily Attendance Report (Detailed)"
        subtitle="Same columns as the eSSL Excel report — backed by our own live punch data, with shift schedule, work duration, overtime, late-by, early-going-by, and the full punch sequence per worker."
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} loading={isRefetching} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              loading={downloading}
              onClick={onDownload}
            >
              Download Excel
            </Button>
          </Space>
        }
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Form.Item label="Date" style={{ marginBottom: 0 }}>
            <DatePicker
              value={dayjs(date)}
              onChange={(d) => setDate(d?.format('YYYY-MM-DD') ?? dayjs().format('YYYY-MM-DD'))}
              format="DD-MMM-YYYY"
            />
          </Form.Item>
          <Form.Item label="Search" style={{ marginBottom: 0 }}>
            <Input.Search
              placeholder="Name / emp code / biometric / dept"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </Form.Item>
          <Form.Item label="Only Present" style={{ marginBottom: 0 }} tooltip="Hide workers with zero punches that day">
            <Switch
              checked={onlyPresent}
              onChange={setOnlyPresent}
            />
          </Form.Item>
        </Space>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space size="large" wrap>
          {/* Pills are clickable filters. A pill clicked again clears that
              filter. The Total pill always clears all pill filters. */}
          <Tag
            color="blue"
            style={{ fontSize: 13, padding: '4px 10px', cursor: 'pointer' }}
            onClick={() => { setStatusPillFilter(null); setAnomaliesOnly(false) }}
          >
            Total: <b>{totalRows}</b>
          </Tag>
          <Tag
            color="cyan"
            style={{
              fontSize: 13, padding: '4px 10px', cursor: 'pointer',
              opacity: statusPillFilter && statusPillFilter !== 'Working' ? 0.5 : 1,
              outline: statusPillFilter === 'Working' ? '2px solid #08979c' : undefined,
            }}
            onClick={() => setStatusPillFilter(statusPillFilter === 'Working' ? null : 'Working')}
          >
            Working now: <b>{workingCount}</b>
          </Tag>
          <Tag
            color="green"
            style={{
              fontSize: 13, padding: '4px 10px', cursor: 'pointer',
              opacity: statusPillFilter && statusPillFilter !== 'Present' ? 0.5 : 1,
              outline: statusPillFilter === 'Present' ? '2px solid #389e0d' : undefined,
            }}
            onClick={() => setStatusPillFilter(statusPillFilter === 'Present' ? null : 'Present')}
          >
            Present: <b>{presentCount}</b>
          </Tag>
          <Tag
            color="gold"
            style={{
              fontSize: 13, padding: '4px 10px', cursor: 'pointer',
              opacity: statusPillFilter && statusPillFilter !== 'Half Day' ? 0.5 : 1,
              outline: statusPillFilter === 'Half Day' ? '2px solid #d48806' : undefined,
            }}
            onClick={() => setStatusPillFilter(statusPillFilter === 'Half Day' ? null : 'Half Day')}
          >
            Half Day: <b>{halfDayCount}</b>
          </Tag>
          {!onlyPresent && (
            <Tag
              color="red"
              style={{
                fontSize: 13, padding: '4px 10px', cursor: 'pointer',
                opacity: statusPillFilter && statusPillFilter !== 'Absent' ? 0.5 : 1,
                outline: statusPillFilter === 'Absent' ? '2px solid #cf1322' : undefined,
              }}
              onClick={() => setStatusPillFilter(statusPillFilter === 'Absent' ? null : 'Absent')}
            >
              Absent: <b>{absentCount}</b>
            </Tag>
          )}
          {anomalyCount > 0 && (
            <Tooltip title={
              anomaliesOnly
                ? 'Showing only anomaly rows — click to clear filter'
                : `Click to filter — ${anomalyCount} workers with odd-punch anomalies (unpaired IN/OUT)`
            }>
              <Tag
                color={anomaliesOnly ? 'red' : 'orange'}
                style={{
                  fontSize: 13, padding: '4px 10px', cursor: 'pointer',
                  outline: anomaliesOnly ? '2px solid #cf1322' : undefined,
                }}
                onClick={() => setAnomaliesOnly(!anomaliesOnly)}
              >
                Anomalies: <b>{anomalyCount}</b> ⚠️
                {anomaliesOnly && ' (filtered)'}
              </Tag>
            </Tooltip>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            ( * suffix = odd-punch anomaly — unpaired IN or OUT in the day's sequence )
          </Text>
          {(anomaliesOnly || statusPillFilter) && (
            <Button
              size="small"
              type="link"
              onClick={() => { setAnomaliesOnly(false); setStatusPillFilter(null) }}
            >
              Clear filters
            </Button>
          )}
        </Space>
      </Card>

      <Table<DailyAttendanceRow>
        rowKey="emp_code"
        loading={isLoading}
        dataSource={filtered}
        columns={columns}
        size="small"
        scroll={{ x: 1900 }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
      />
    </div>
  )
}
// silence unused import warnings if Download icon variant isn't directly used in the body
void DownloadOutlined
